import type { TicketRow } from "./types";
import { CLOSED_STATUSES } from "./types";

export type StatusKind = "new" | "new-team" | "in-progress" | "waiting-customer" | "waiting-cs" | "closed" | "other";

// The team names as the Sheet and the Feature map spell them.
const TEAMS: readonly (readonly [string, string])[] = [
  ["platform", "Platform"],
  ["statsquad", "StatSquad"],
  ["sharesquad", "ShareSquad"],
  ["coco", "CoCo"],
];

const CLOSED = new Set(CLOSED_STATUSES.map((status) => status.toLowerCase()));

// The tracker has a column per team and stage ("New", "New - CoCo", "Platform in progress", "ShareSquad Waiting on CS"), so the stage is read from the words.
// Only the plain "New" column is where a ticket waits to be picked up; "New - <team>" is a ticket already routed to a team.
export function statusKind(status: string): StatusKind {
  const label = status.trim().toLowerCase();
  if (CLOSED.has(label) || label.startsWith("complete")) {
    return "closed";
  }
  if (label === "new") {
    return "new";
  }
  if (/^new\b/.test(label)) {
    return "new-team";
  }
  if (label.includes("waiting on customer")) {
    return "waiting-customer";
  }
  if (label.includes("waiting on cs")) {
    return "waiting-cs";
  }
  if (label.includes("in progress")) {
    return "in-progress";
  }
  return "other";
}

// The team named in a column, if there is one ("Waiting on Customer" and the closed columns name none).
export function teamInStatus(status: string): string | undefined {
  const label = status.toLowerCase();
  return TEAMS.find(([key]) => label.includes(key))?.[1];
}

// What the tracker shows now for a ticket, applied to its row:
// picked up, and the engineer, when it first reaches a column an engineer works in (Triage Desk routes tickets into the "New - <team>" columns itself,
// so a move between New columns is routing, not picking up), the first time it enters in progress or a waiting column, and the close date each time it enters a closed column.
// History imported from the Sheet keeps its own picked-up dates, which were set when a person moved a ticket out of New.
export function applyStatus(row: TicketRow, observed: { status: string; at: string; engineer?: string }): TicketRow {
  if (row.status === observed.status) {
    return row;
  }
  const kind = statusKind(observed.status);
  const next: TicketRow = { ...row, status: observed.status };
  const team = teamInStatus(observed.status);
  if (team) {
    next.team = team;
  }
  if (kind !== "new" && kind !== "new-team") {
    next.pickedUpAt ??= observed.at;
    next.engineer ??= observed.engineer;
  }
  if (kind === "in-progress") {
    next.inProgressAt ??= observed.at;
  } else if (kind === "waiting-customer") {
    next.waitingCustomerAt ??= observed.at;
  } else if (kind === "waiting-cs") {
    next.waitingCsAt ??= observed.at;
  } else if (kind === "closed" && !(row.status && statusKind(row.status) === "closed")) {
    next.closedAt = observed.at;
  }
  return next;
}

// Whether the sync still needs to watch a ticket: anything not closed, and tickets closed in the last two weeks in case they are reopened.
export function isWatched(row: TicketRow, now: Date): boolean {
  if (!row.listItemId) {
    return false;
  }
  if (!row.status || statusKind(row.status) !== "closed") {
    return true;
  }
  return row.closedAt !== undefined && now.getTime() - new Date(row.closedAt).getTime() < 14 * 24 * 60 * 60 * 1000;
}
